'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth';
import { usePricing } from '@/components/PricingDisplay';
import { detectCurrency, detectCurrencyFromLocale, type Currency } from '@/lib/currency-detection';
import { trackSignup, trackEvent, getUTMParams, type SignupTier } from '@/lib/analytics';
import { isIOS } from '@/lib/platform';

const CURRENCIES: { code: Currency; label: string; symbol: string; flag: string }[] = [
  { code: 'USD', label: 'United States', symbol: '$', flag: '\u{1F1FA}\u{1F1F8}' },
  { code: 'EUR', label: 'Europe', symbol: '\u20AC', flag: '\u{1F1EA}\u{1F1FA}' },
  { code: 'GBP', label: 'United Kingdom', symbol: '\u00A3', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: 'AUD', label: 'Australia', symbol: 'A$', flag: '\u{1F1E6}\u{1F1FA}' },
];

export default function PricingPage() {
  const { isAuthenticated, login } = useAuth();
  // Initialize with browser locale (fast), then upgrade to IP geolocation (accurate)
  const [currency, setCurrency] = useState<Currency>(() => {
    if (typeof window !== 'undefined') {
      return detectCurrencyFromLocale();
    }
    return 'USD';
  });
  const [navigatingPlan, setNavigatingPlan] = useState<string | null>(null);
  const [showComingSoon, setShowComingSoon] = useState(false);
  const { prices, loading, error } = usePricing(`/api/public/stripe/prices?currency=${currency}`);
  const router = useRouter();

  // Upgrade to IP-based geolocation after mount
  useEffect(() => {
    detectCurrency().then((detectedCurrency) => {
      // Only update if different to avoid unnecessary re-fetch
      if (detectedCurrency !== currency) {
        setCurrency(detectedCurrency);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount

  // Get dynamic prices from Stripe (no defaults - show loading state instead)
  const freePrice = prices?.freeMonthly?.displayAmount;
  const proPrice = prices?.proMonthly?.displayAmount;
  const teamPrice = prices?.teamMonthly?.displayAmount;
  const teamExtraPrice = prices?.teamExtraMonthly?.displayAmount;

  const plans = [
    {
      name: 'Free',
      price: freePrice,
      period: 'forever',
      description: 'Perfect for getting started with CTO leadership content',
      features: [
        { text: 'All Insights (blog posts)', included: true },
        { text: '8 foundational frameworks', included: true },
        { text: 'Performance reviews, security, incidents', included: true },
        { text: 'Bookmark and save content', included: true },
        { text: 'Basic activity tracking', included: true },
        { text: '2 active learning paths', included: true },
        { text: 'Email newsletter', included: true },
        { text: 'Community access', included: true },
        { text: '11 advanced playbooks (First 90 Days, etc)', included: false },
        { text: 'Unlimited learning paths', included: false },
        { text: 'Path completion certificates', included: false },
        { text: 'Download templates & resources', included: false },
        { text: 'Priority support', included: false },
      ],
      cta: isAuthenticated ? 'Current Plan' : 'Sign Up Free',
      ctaLink: isAuthenticated ? '/dashboard' : null,
      highlighted: false,
    },
    {
      name: 'Pro',
      price: proPrice,
      period: 'per month',
      description: 'For CTOs serious about continuous learning and growth',
      features: [
        { text: 'Everything in Free', included: true },
        { text: '11 advanced playbooks & frameworks', included: true },
        { text: 'First 90 Days as CTO (846 lines)', included: true },
        { text: 'Engineering Strategy & Roadmap (795 lines)', included: true },
        { text: 'Team Scaling Playbook (592 lines)', included: true },
        { text: 'Career Ladders & Progression', included: true },
        { text: 'Budget Planning & Vendor Management', included: true },
        { text: 'Unlimited learning paths', included: true },
        { text: 'Path completion certificates', included: true },
        { text: 'Download templates & resources', included: true },
        { text: 'Priority email support', included: true },
        { text: 'Ad-free experience', included: true },
      ],
      cta: 'Upgrade to Pro',
      ctaLink: isAuthenticated ? '/dashboard/billing' : null,
      highlighted: true,
      badge: 'Most Popular',
    },
    {
      name: 'Team',
      price: teamPrice,
      period: 'per month',
      description: 'For leadership teams building exceptional engineering organizations',
      extraSeatsPrice: teamExtraPrice,
      features: [
        { text: 'Everything in Pro', included: true },
        { text: 'Up to 5 team members', included: true },
        { text: `Additional seats: ${teamExtraPrice}/month each`, included: true },
        { text: 'Shared learning paths & progress', included: true },
        { text: 'Team analytics dashboard', included: true },
        { text: 'Custom learning path creation', included: true },
        { text: 'Bulk certificate generation', included: true },
        { text: 'Team referral rewards', included: true },
        { text: 'Dedicated account manager', included: true },
        { text: 'Priority phone & email support', included: true },
        { text: 'Quarterly strategy sessions', included: true },
      ],
      cta: 'Upgrade to Team',
      ctaLink: isAuthenticated ? '/dashboard/billing' : null,
      highlighted: false,
    },
  ];

  const handleCTAClick = (plan: typeof plans[0]) => {
    // Show coming soon modal instead of proceeding
    setShowComingSoon(true);

    // Track the click event for analytics
    const tier = plan.name.toLowerCase() as SignupTier;
    const utmParams = getUTMParams();

    trackEvent('pricing_cta_clicked_coming_soon', {
      event_category: 'conversion',
      event_label: plan.name,
      tier,
      source: 'pricing_page',
      is_authenticated: isAuthenticated,
      campaign: utmParams.campaign,
    });
  };

  // iOS native: hide pricing (Apple Guideline 3.1.1 — no external payment references)
  if (isIOS()) {
    return (
      <main className="min-h-screen bg-background py-16">
        <div className="container mx-auto px-6 max-w-3xl text-center">
          <h1 className="text-4xl font-bold text-foreground mb-6">
            Manage Your Subscription
          </h1>
          <p className="text-lg text-foreground-secondary mb-8">
            To view pricing plans and manage your subscription, please visit{' '}
            <span className="text-accent font-semibold">theartofcto.com</span>{' '}
            in your web browser.
          </p>
          <Link
            href="/dashboard"
            className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent/90 transition-colors"
          >
            Go to Dashboard
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background py-16">
      <div className="container mx-auto px-6 max-w-7xl">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-foreground mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-foreground-secondary max-w-3xl mx-auto">
            Choose the plan that fits your needs. All plans include access to our core content library.
          </p>

          {/* Currency Selector */}
          <div className="mt-6 flex items-center justify-center gap-3">
            <span className="text-foreground-secondary text-sm">Region:</span>
            <div className="relative">
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value as Currency)}
                className="appearance-none bg-surface border border-border text-foreground pl-4 pr-10 py-2 rounded-lg text-sm font-semibold hover:border-accent focus:border-accent focus:outline-none focus:ring-2 focus:ring-ring/20 transition-all cursor-pointer"
              >
                {CURRENCIES.map((curr) => (
                  <option key={curr.code} value={curr.code} className="bg-background text-foreground">
                    {curr.flag} {curr.label} ({curr.symbol})
                  </option>
                ))}
              </select>
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <svg className="w-4 h-4 text-foreground-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>

          {/* Pricing Loading/Error States */}
          {loading && (
            <div className="mt-4 text-foreground-tertiary text-sm">
              Loading current pricing...
            </div>
          )}
          {error && !loading && (
            <div className="mt-4 text-warning text-sm">
              Error loading prices. Please try again later.
            </div>
          )}
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`relative rounded-xl p-8 ${
                plan.highlighted
                  ? 'bg-gradient-to-br from-accent-muted to-surface border-2 border-accent scale-105 shadow-2xl'
                  : 'bg-surface border border-border'
              }`}
            >
              {/* Badge */}
              {plan.badge && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-accent text-accent-foreground px-4 py-1 rounded-full text-sm font-bold">
                    {plan.badge}
                  </span>
                </div>
              )}

              {/* Plan Name */}
              <h2 className="text-2xl font-bold text-foreground mb-2">{plan.name}</h2>

              {/* Price */}
              <div className="mb-4">
                {loading ? (
                  <div className="h-16 flex items-center gap-3">
                    <svg
                      className="animate-spin h-10 w-10 text-accent"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span className="text-xl text-foreground-tertiary">Loading pricing...</span>
                  </div>
                ) : (
                  <>
                    <span className="text-5xl font-bold text-accent">{plan.price}</span>
                    <span className="text-foreground-tertiary ml-2">/ {plan.period}</span>
                  </>
                )}
              </div>

              {/* Description */}
              <p className="text-foreground-secondary mb-6 min-h-[3rem]">{plan.description}</p>

              {/* CTA Button */}
              <button
                onClick={() => handleCTAClick(plan)}
                className={`w-full py-3 rounded-lg font-semibold transition-all mb-8 cursor-pointer flex items-center justify-center gap-2 ${
                  plan.highlighted
                    ? 'bg-accent text-accent-foreground hover:bg-accent-hover shadow-lg hover:shadow-xl'
                    : 'bg-background border border-border text-accent hover:border-accent hover:bg-accent-muted'
                }`}
              >
                {plan.cta}
              </button>

              {/* Features List */}
              <ul className="space-y-3">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className={`mt-1 ${feature.included ? 'text-success' : 'text-foreground-muted'}`}>
                      {feature.included ? '\u2713' : '\u2014'}
                    </span>
                    <span className={feature.included ? 'text-foreground' : 'text-foreground-muted line-through'}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-foreground mb-8 text-center">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-surface border border-border rounded-lg p-6">
              <h3 className="text-xl font-bold text-foreground mb-2">
                Can I upgrade or downgrade anytime?
              </h3>
              <p className="text-foreground-secondary">
                Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately,
                and we&apos;ll prorate your billing accordingly.
              </p>
            </div>

            <div className="bg-surface border border-border rounded-lg p-6">
              <h3 className="text-xl font-bold text-foreground mb-2">
                What payment methods do you accept?
              </h3>
              <p className="text-foreground-secondary">
                We accept all major credit cards (Visa, Mastercard, American Express) via Stripe.
                Enterprise customers can arrange invoice billing.
              </p>
            </div>

            <div className="bg-surface border border-border rounded-lg p-6">
              <h3 className="text-xl font-bold text-foreground mb-2">
                How do referral rewards work?
              </h3>
              <p className="text-foreground-secondary">
                Pro and Team members can refer friends and earn 1 free month for every friend who upgrades
                to a paid plan. Your friend also gets 2 weeks free! Credits are valid for 1 year.
              </p>
            </div>

            <div className="bg-surface border border-border rounded-lg p-6">
              <h3 className="text-xl font-bold text-foreground mb-2">
                What&apos;s included in learning path certificates?
              </h3>
              <p className="text-foreground-secondary">
                Pro and Team members receive verifiable completion certificates for all learning paths.
                Certificates include a unique verification code and shareable URL for LinkedIn and portfolios.
              </p>
            </div>

            <div className="bg-surface border border-border rounded-lg p-6">
              <h3 className="text-xl font-bold text-foreground mb-2">
                Is there a refund policy?
              </h3>
              <p className="text-foreground-secondary">
                We offer a 14-day money-back guarantee on all paid plans. If you&apos;re not satisfied,
                contact us within 14 days for a full refund, no questions asked.
              </p>
            </div>

            <div className="bg-surface border border-border rounded-lg p-6">
              <h3 className="text-xl font-bold text-foreground mb-2">
                Do you offer enterprise plans?
              </h3>
              <p className="text-foreground-secondary">
                Yes! For organizations with more than 10 members, we offer custom enterprise plans with
                additional features, SSO, advanced analytics, and dedicated support.{' '}
                <Link href="/contact" className="text-accent hover:underline">
                  Contact us
                </Link>{' '}
                for details.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            Ready to Level Up Your Leadership?
          </h2>
          <p className="text-xl text-foreground-secondary mb-8">
            Join hundreds of CTOs mastering technical leadership
          </p>
          <button
            onClick={() => setShowComingSoon(true)}
            className="bg-accent text-accent-foreground px-8 py-4 rounded-lg font-bold text-lg hover:bg-accent-hover transition-colors shadow-lg cursor-pointer inline-flex items-center justify-center gap-2"
          >
            {isAuthenticated ? 'Upgrade Now' : 'Get Started Free'}
          </button>
        </div>
      </div>

      {/* Coming Soon Modal */}
      {showComingSoon && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-background border-2 border-accent rounded-xl max-w-lg w-full p-8 relative animate-fadeIn">
            <button
              onClick={() => setShowComingSoon(false)}
              className="absolute top-4 right-4 text-foreground-tertiary hover:text-foreground transition-colors"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="text-center">
              <div className="text-6xl mb-4">&#x1F6A7;</div>
              <h2 className="text-3xl font-bold text-accent mb-4">Coming Soon!</h2>
              <p className="text-xl text-foreground mb-6">
                Paid subscriptions and premium features are launching soon.
              </p>
              <p className="text-foreground-secondary mb-8">
                In the meantime, all tools and content are completely free to use.
                Sign up for our newsletter to be notified when premium features go live!
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => setShowComingSoon(false)}
                  className="w-full bg-accent text-accent-foreground px-6 py-3 rounded-lg font-semibold hover:bg-accent-hover transition-colors"
                >
                  Continue Exploring (Free)
                </button>
                <Link
                  href="/newsletter"
                  onClick={() => setShowComingSoon(false)}
                  className="block w-full bg-surface border border-border text-accent px-6 py-3 rounded-lg font-semibold hover:border-accent hover:bg-accent-muted transition-colors"
                >
                  Subscribe to Newsletter
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
